import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip, { ChipProps } from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import GppGoodOutlined from '@mui/icons-material/GppGoodOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import moment from 'moment';
import { Formatter } from '@omnigenbiodata/ui';
import { Participant } from '../../../../../../../../core/types/common.types';
import DetailRow from '../DetailRow';

interface ParticipantComplianceProps {
  participant: Participant;
}

type ComplianceStatus = 'pass' | 'fail' | 'warning' | 'not-applicable';

interface ComplianceCheck {
  label: string;
  status: ComplianceStatus;
  finding: ReactNode;
  detail?: ReactNode;
}

interface ParticipantEvent {
  name: string;
  date: string | null | undefined;
}

function getStatusColour(status: ComplianceStatus): ChipProps['color'] {
  if (status === 'pass') {
    return 'success';
  }

  if (status === 'fail') {
    return 'error';
  }

  if (status === 'warning') {
    return 'warning';
  }

  return 'default';
}

function getStatusLabel(status: ComplianceStatus) {
  if (status === 'pass') {
    return 'Pass';
  }

  if (status === 'fail') {
    return 'Fail';
  }

  if (status === 'warning') {
    return 'Warning';
  }

  return 'N/A';
}

function getStatusIcon(status: ComplianceStatus) {
  if (status === 'pass') {
    return <CheckCircleOutlined color="success" fontSize="small" />;
  }

  if (status === 'fail') {
    return <ErrorOutlineOutlined color="error" fontSize="small" />;
  }

  if (status === 'warning') {
    return <WarningAmberOutlined color="warning" fontSize="small" />;
  }

  return <WarningAmberOutlined color="disabled" fontSize="small" />;
}

function formatDateTime(value: string | null | undefined) {
  return value ? Formatter({ input: value, format: 'friendlyDateTime' }) : 'Not recorded';
}

function isSameOrBefore(date: string | null | undefined, comparisonDate: string | null | undefined) {
  if (!date || !comparisonDate) {
    return null;
  }

  const parsedDate = moment(date);
  const parsedComparisonDate = moment(comparisonDate);

  if (!parsedDate.isValid() || !parsedComparisonDate.isValid()) {
    return null;
  }

  return parsedDate.isSameOrBefore(parsedComparisonDate);
}

function calculateAgeAtDate(dob: string | null | undefined, comparisonDate: string | null | undefined) {
  if (!dob || !comparisonDate) {
    return null;
  }

  const birthDate = moment(dob);
  const enrolmentDate = moment(comparisonDate);

  if (!birthDate.isValid() || !enrolmentDate.isValid()) {
    return null;
  }

  return enrolmentDate.diff(birthDate, 'years');
}

function getConsentAnswers(participant: Participant) {
  return [
    participant.consent1,
    participant.consent2,
    participant.consent3,
    participant.consent4,
    participant.consent5,
    participant.consent6,
    participant.consent7,
    participant.consent8,
    participant.consent9,
  ];
}

function getEventsAfterWithdrawal(participant: Participant): ParticipantEvent[] {
  if (!participant.withdrawalTs) {
    return [];
  }

  const withdrawalDate = moment(participant.withdrawalTs);

  const events: ParticipantEvent[] = [
    {
      name: 'EHR data arrived',
      date: participant.ehr?.dataHasArrivedTs,
    },
    {
      name: 'EHR status updated',
      date: participant.ehr?.ehrUpdatedTs,
    },
    {
      name: 'Sample order updated',
      date: participant.sample?.orderUpdatedTs,
    },
    {
      name: 'Sample linked',
      date: participant.sample?.sampleLinkTs,
    },
    {
      name: 'Sample received',
      date: participant.sample?.sampleReceivedTs,
    },
    {
      name: 'Sample shipped',
      date: participant.lab?.shippedDate,
    },
  ];

  return events.filter((event) => {
    if (!event.date) {
      return false;
    }

    const eventDate = moment(event.date);

    return eventDate.isValid() && eventDate.isAfter(withdrawalDate);
  });
}

function renderStatusChip(status: ComplianceStatus) {
  return <Chip label={getStatusLabel(status)} color={getStatusColour(status)} size="small" />;
}

function ComplianceCheckRow({ check }: { check: ComplianceCheck }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1.5,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {getStatusIcon(check.status)}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body1" component="h3" sx={{ fontWeight: 'bold' }}>
            {check.label}
          </Typography>

          {renderStatusChip(check.status)}
        </Box>

        <Typography variant="body2" component="p">
          {check.finding}
        </Typography>

        {check.detail && (
          <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
            {check.detail}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function buildComplianceChecks(participant: Participant): ComplianceCheck[] {
  const ageAtEnrolment = calculateAgeAtDate(participant.dob, participant.enrolmentTs);
  const consentAnswers = getConsentAnswers(participant);
  const unansweredConsentIndexes = consentAnswers
    .map((answer, index) => ({ answer, consentNumber: index + 1 }))
    .filter(({ answer }) => answer === null || answer === undefined)
    .map(({ consentNumber }) => consentNumber);

  const eventsAfterWithdrawal = getEventsAfterWithdrawal(participant);
  const consentFormBeforeEnrolment = isSameOrBefore(participant.consentFormDate, participant.enrolmentTs);
  const pisBeforeEnrolment = isSameOrBefore(participant.pisDate, participant.enrolmentTs);

  return [
    {
      label: 'Consent documents were dated before enrolment',
      status:
        consentFormBeforeEnrolment === null || pisBeforeEnrolment === null
          ? 'warning'
          : consentFormBeforeEnrolment && pisBeforeEnrolment
            ? 'pass'
            : 'fail',
      finding:
        consentFormBeforeEnrolment === null || pisBeforeEnrolment === null
          ? 'Unable to fully confirm consent document dates against enrolment.'
          : consentFormBeforeEnrolment && pisBeforeEnrolment
            ? 'Consent form date and PIS date are on or before enrolment.'
            : 'One or more consent document dates are after enrolment.',
      detail: (
        <>
          Consent form date: {formatDateTime(participant.consentFormDate)}. PIS date:{' '}
          {formatDateTime(participant.pisDate)}. Enrolment: {formatDateTime(participant.enrolmentTs)}.
        </>
      ),
    },
    {
      label: 'Participant was 18 or over at enrolment',
      status: ageAtEnrolment === null ? 'warning' : ageAtEnrolment >= 18 ? 'pass' : 'fail',
      finding:
        ageAtEnrolment === null
          ? 'Unable to calculate age at enrolment.'
          : `Participant was ${ageAtEnrolment} at enrolment.`,
      detail: (
        <>
          DOB: {formatDateTime(participant.dob)}. Enrolment: {formatDateTime(participant.enrolmentTs)}.
        </>
      ),
    },
    {
      label: 'Site was initiated',
      status: participant.site?.isInitiated ? 'pass' : 'fail',
      finding: participant.site?.isInitiated ? 'Site is marked as initiated.' : 'Site is not marked as initiated.',
      detail: (
        <>
          Site: {participant.site?.siteName || 'Not recorded'}. ODS Code: {participant.site?.odsCode || 'Not recorded'}.
        </>
      ),
    },
    {
      label: 'All consent questions answered',
      status: unansweredConsentIndexes.length === 0 ? 'pass' : 'fail',
      finding:
        unansweredConsentIndexes.length === 0
          ? 'Consent questions 1–9 all have recorded answers.'
          : `Missing answer for consent question${unansweredConsentIndexes.length > 1 ? 's' : ''}: ${unansweredConsentIndexes.join(
              ', ',
            )}.`,
      detail: 'This checks whether each consent question has a true or false value recorded.',
    },
    {
      label: 'No activity after withdrawal',
      status: participant.withdrawalTs ? (eventsAfterWithdrawal.length === 0 ? 'pass' : 'fail') : 'not-applicable',
      finding: !participant.withdrawalTs
        ? 'Participant has not been withdrawn.'
        : eventsAfterWithdrawal.length === 0
          ? 'No post-withdrawal activity detected.'
          : `${eventsAfterWithdrawal.length} event${eventsAfterWithdrawal.length > 1 ? 's were' : ' was'} recorded after withdrawal.`,
      detail: participant.withdrawalTs ? (
        <>
          Withdrawal: {formatDateTime(participant.withdrawalTs)}
          {eventsAfterWithdrawal.length > 0 && (
            <>
              {' '}
              Post-withdrawal events:{' '}
              {eventsAfterWithdrawal.map((event) => `${event.name} (${formatDateTime(event.date)})`).join(', ')}.
            </>
          )}
        </>
      ) : undefined,
    },
  ];
}

function ParticipantCompliance({ participant }: ParticipantComplianceProps) {
  const checks = buildComplianceChecks(participant);
  const failedChecks = checks.filter((check) => check.status === 'fail').length;
  const warningChecks = checks.filter((check) => check.status === 'warning').length;

  return (
    <Box mb={2}>
      <Paper elevation={4}>
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <GppGoodOutlined color={failedChecks > 0 ? 'error' : warningChecks > 0 ? 'warning' : 'success'} />

            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', m: 0 }}>
              Compliance Checks
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <DetailRow label="Failed Checks" value={failedChecks} />
            <DetailRow label="Warnings" value={warningChecks} />
          </Box>

          <Divider sx={{ my: 1 }} />

          {checks.map((check, index) => (
            <Box key={check.label}>
              <ComplianceCheckRow check={check} />

              {index < checks.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantCompliance;
